/* eslint-disable no-bitwise */

import { BitReader } from '../aac/bitReader';
import { parseSequenceparameterSet } from '../h264/h264';
import { invariant } from '../util/invariant';

const ES_DescrTag = 0x03;
const DecoderConfigDescrTag = 0x04;
const DecSpecificInfoTag = 0x05;
const SLConfigDescrTag = 0x06;
const ContentIdentDescrTag = 0x07;
const SupplContentIdentDescrTag = 0x08;
const IPI_DescrPointerTag = 0x09;
const IPMP_DescrPointerTag = 0x0A;
const QoS_DescrTag = 0x0C;
const RegistrationDescrTag = 0x0D;
const ExtensionProfileLevelDescrTag = 0x13;
const ProfileLevelIndicationIndexDescrTag = 0x14;
const LanguageDescrTag = 0x43;
const ExtSLConfigDescrTag = 0x64;
const DependencyPointerTag = 0x67;
const DependencyMarkerTag = 0x68;

export interface Mp4Box {
  type: string;
  size: number;
  start: number;
  children: Mp4Box[];
}


export interface FullBox extends Mp4Box {
  version: number;
  flags: number;
}


export interface TrackHeaderBox extends FullBox {
  type: 'tkhd';
  creation_time: number;
  modification_time: number;
  track_id: number;
  duration: number;
  layer: number;
  alternate_group: number;
  volume: number;
  matrix: number[];
  width: number;
  height: number;
}


export interface EditListBoxEntry {
  segment_duration: number;
  media_time: number;
  media_rate_integer: number;
  media_rate_fraction: number;
}

export interface EditListBox extends FullBox {
  type: 'elst';
  entries: EditListBoxEntry[];
}


export interface MovieHeaderBox extends FullBox {
  type: 'mvhd';
  creation_time: number;
  modification_time: number;
  timescale: number;
  duration: number;
  rate: number;
  volume: number;
  matrix: number[];
  next_track_id: number;
}


interface FileTypeBox extends Mp4Box {
  type: 'ftyp';
  major_brand: string;
  minor_version: number;
  compatible_brands: string[];
}


export interface SampleEntry extends Mp4Box {
  data_reference_index: number;
}


export interface VisualSampleEntry extends SampleEntry {
  width: number;
  height: number;
  horizresolution: number;
  vertresolution: number;
  frame_count: number;
  compressorName: string;
  depth: number;
}


export interface Avc1VisualSampleEntry extends VisualSampleEntry {
  type: 'avc1';
}


export interface AVCDecoderConfigurationRecord {
  configurationVersion: number;
  AVCProfileIndication: number;
  profile_compatibility: number;
  AVCLevelIndication: number;
  lengthSizeMinusOne: number;
  SPS_nalus: number[][];
  PPS_nalus: number[][];
  chroma_format?: number;
  bit_depth_luma_minus8?: number;
  bit_depth_chroma_minus8?: number;
  SPS_ext_nalus?: number[][];
}


export function getCodecString(avcCBox: AvcC): string {
  const profile = avcCBox.AVCProfileIndication.toString(16).padStart(2, '0');
  const level = avcCBox.AVCLevelIndication.toString(16).padStart(2, '0');
  const compat = avcCBox.profile_compatibility.toString(16).padStart(2, '0');
  return `avc1.${profile}${compat}${level}`;
}


export function getProgressive(avcCBox: AvcC): boolean {
  if (avcCBox.SPS_nalus.length === 0) {
    // Not sure if it can actually happen not to have any SPS,
    // but it seems safe to assume progressive in that case
    return true;
  }

  const result = parseSequenceparameterSet(new Uint8Array(avcCBox.SPS_nalus[0]!));
  return result.frame_mbs_only_flag;
}


export interface AvcC extends Mp4Box, AVCDecoderConfigurationRecord {
  type: 'avcC';
  // The raw avcC content, since we may need to pass it to the web codec
  // video decoder as the "extradata" byte property
  avcC: number[];
}

export interface ColorInformationBox extends Mp4Box {
  type: 'colr';
  color_type: string;

  // The following are defined when color_type is 'nclx':
  colour_primaries?: number;
  transfer_characteristics?: number;
  matrix_coefficients?: number;
  full_range_flag?: number;
}

export interface PixelAspectRatioBox extends Mp4Box {
  type: 'pasp';
  hSpacing: number;
  vSpacing: number;
}

export interface AudioSampleEntry extends SampleEntry {
  channelcount: number;
  samplesize: number;
  samplerate: number;
}

export interface FLACMetadataBlock {
  LastMetadataBlockFlag: boolean;
  BlockType: number;
  BlockData: number[];
}

export interface Mp4aBox extends AudioSampleEntry {
  type: 'mp4a';
}


export interface BaseDescriptor {
  tag: number;
}


export interface BaseDescriptorWithSize extends BaseDescriptor {
  sizeOfInstance: number;
}


export interface DecoderSpecificInfo extends BaseDescriptor {
  tag: typeof DecSpecificInfoTag;
}

export interface AudioSpecificConfig extends DecoderSpecificInfo {
  audioObjectType: number;
  samplingFrequencyIndex: number;
  channelConfiguration: number;
}

export interface JPEG2000_DecoderConfig extends DecoderSpecificInfo {
  height: number;
  width: number;
  nc: number;
  BPC: number;
  C: number;
  UnkC: number;
  IPR: number;
}

export interface AVCDecoderSpecificInfo extends DecoderSpecificInfo {
  config: AVCDecoderConfigurationRecord;
}

export interface OCIDecoderConfiguration extends DecoderSpecificInfo {
  versionLabel: 1;
}

export interface JPEG_DecoderConfig extends DecoderSpecificInfo {
  headerLength: number;
  Xdensity: number;
  Ydensity: number;
  numComponents: number;
}

export interface UIConfig extends DecoderSpecificInfo {
  deviceName: string;
  devSpecInfo: number[];
}

export type ExtendedBIFSConfig = BaseDescriptor;

export interface BIFSConfigEx extends DecoderSpecificInfo {
  extBIFSConfig: ExtendedBIFSConfig;
}

export type AFXExtDescriptor = BaseDescriptor;

export interface AFXConfig extends DecoderSpecificInfo {
  afxext: AFXExtDescriptor;
}

export interface ProfileLevelIndicationIndexDescriptor extends BaseDescriptor {
  tag: typeof ProfileLevelIndicationIndexDescrTag;
  profileLevelIndicationIndex: number;
}

export interface DecoderConfigDescriptor extends BaseDescriptor {
  tag: typeof DecoderConfigDescrTag;
  objectTypeIndication: number;
  streamType: number;
  upStream: number;
  bufferSizeDB: number;
  maxBitrate: number;
  avgBitrate: number;
  decSpecificInfo: DecoderSpecificInfo[];
  profileLevelIndicationIndexDescr: ProfileLevelIndicationIndexDescriptor[];
}

export interface SLConfigDescriptor extends BaseDescriptor {
  tag: (typeof SLConfigDescrTag) | (typeof ExtSLConfigDescrTag);
  useAccessUnitStartFlag: number;
  useAccessUnitEndFlag: number;
  useRandomAccessPointFlag: number;
  hasRandomAccessUnitsOnlyFlag: number;
  usePaddingFlag: number;
  useTimeStampsFlag: number;
  useIdleFlag: number;
  durationFlag: number;
  timeStampResolution: number | undefined;
  OCRResolution: number | undefined;
  timeStampLength: number;
  OCRLength: number | undefined;
  AU_Length: number;
  instantBitrateLength: number | undefined;
  degradationPriorityLength: number;
  AU_seqNumLength: number;
  packetSeqNumLength: number;

  // Present if durationFlag is 1
  timeScale?: number;
  accessUnitDuration?: number;
  compositionUnitDuration?: number;

  // Present if useTimeStampsFlag is 0
  startDecodingTimeStamp?: number;
  startCompositionTimeStamp?: number;
}

export type SLExtensionDescriptor = BaseDescriptor;

export interface DependencyPointer extends SLExtensionDescriptor {
  tag: typeof DependencyPointerTag;
  mode: number;
  dependencyLength: number;
  ESID?: number;
}

export interface MarkerDescriptor extends SLExtensionDescriptor {
  tag: typeof DependencyMarkerTag;
  markerLength: number;
}

export interface ExtendedSLConfigDescriptor extends SLConfigDescriptor {
  tag: typeof ExtSLConfigDescrTag;
  slextDescr: SLExtensionDescriptor[];
}

export interface IPI_DescrPointer extends BaseDescriptor {
  tag: typeof IPI_DescrPointerTag;
  IPI_ES_Id: number;
}

export type IP_IdentificationDataSet = BaseDescriptor;

export interface ContentIdentificationDescriptor extends IP_IdentificationDataSet {
  tag: typeof ContentIdentDescrTag;
  protectedContent: number;
  contentType?: number;
  contentIdentifierType?: number;
  contentIdentifier?: number[];
}

export interface SupplementaryContentIdentificationDescriptor extends IP_IdentificationDataSet {
  tag: typeof SupplContentIdentDescrTag;
  languageCode: string;
  supplContentIdentifierTitle: number[];
  supplContentIdentifierValue: number[];
}

export interface IPMP_DescriptorPointer extends BaseDescriptor {
  tag: typeof IPMP_DescrPointerTag;
  IPMP_DescriptorID: number;
  IPMP_DescriptorIDEx?: number;
  IPMP_ES_ID?: number;
}

export type OCI_Descriptor = BaseDescriptor;

export interface LanguageDescriptor extends OCI_Descriptor {
  tag: typeof LanguageDescrTag;
  languageCode: string;
}

export type QoS_Qualifier = BaseDescriptor;

export interface QoS_Qualifier_MAX_DELAY extends QoS_Qualifier {
  tag: 0x01;
  MAX_DELAY: number;
}

export interface QoS_Qualifier_PREF_MAX_DELAY extends QoS_Qualifier {
  tag: 0x02;
  PREF_MAX_DELAY: number;
}

export interface QoS_Qualifier_LOSS_PROB extends QoS_Qualifier {
  tag: 0x03;
  LOSS_PROB: number;
}

export interface QoS_Qualifier_MAX_GAP_LOSS extends QoS_Qualifier {
  tag: 0x04;
  MAX_GAP_LOSS: number;
}

export interface QoS_Qualifier_MAX_AU_SIZE extends QoS_Qualifier {
  tag: 0x41;
  MAX_AU_SIZE: number;
}

export interface QoS_Qualifier_AVG_AU_SIZE extends QoS_Qualifier {
  tag: 0x42;
  AVG_AU_SIZE: number;
}

export interface QoS_Qualifier_MAX_AU_RATE extends QoS_Qualifier {
  tag: 0x43;
  MAX_AU_RATE: number;
}

export interface QoS_Qualifier_REBUFFERING_RATIO extends QoS_Qualifier {
  tag: 0x44;
  REBUFFERING_RATIO: number;
}

export interface QoS_Descriptor extends BaseDescriptor {
  tag: typeof QoS_DescrTag;
  qualifiers: QoS_Qualifier[];
}

export interface RegistrationDescriptor extends BaseDescriptor {
  tag: typeof RegistrationDescrTag;
  formatIdentifier: number;
  additionalIdentificationInfo: number[];
}

export type ExtensionDescriptor = BaseDescriptor;

export interface ExtensionProfileLevelDescriptor extends ExtensionDescriptor {
  tag: typeof ExtensionProfileLevelDescrTag;
  profileLevelIndicationIndex: number;
  ODProfileLevelIndication: number;
  sceneProfileLevelIndication: number;
  audioProfileLevelIndication: number;
  visualProfileLevelIndication: number;
  graphicsProfileLevelIndication: number;
  MPEGJProfileLevelIndication: number;
  TextProfileLevelIndication: number;
  _3DCProfileLevelIndication: number;
}

// Defined in ISO/IEC 14496-1:2010(E)
export interface ES_Descriptor extends BaseDescriptor {
  tag: typeof ES_DescrTag;
  ES_ID: number;
  streamPriority: number;
  dependsOn_ES_ID?: number;
  URLstring?: string;
  OCR_ES_Id?: number;
  decConfigDescr: DecoderConfigDescriptor;
  slConfigDescr: SLConfigDescriptor;
  ipiPtr: IPI_DescrPointer[];
  ipIDS: IP_IdentificationDataSet[];
  ipmpDescrPtr: IPMP_DescriptorPointer[];
  langDescr: LanguageDescriptor[];
  qosDescr: QoS_Descriptor[];
  regDescr: RegistrationDescriptor[];
  extDescr: ExtensionDescriptor[];
}

export interface ElementaryStreamDescriptorBox extends FullBox {
  type: 'esds';
  es: ES_Descriptor;
}

export interface FLACSpecificBox extends FullBox {
  type: 'dfLa';
  flacMetadataBlocks: FLACMetadataBlock[];
}


interface BitRateBox extends Mp4Box {
  type: 'btrt';
  bufferSizeDB: number;
  maxBitrate: number;
  avgBitrate: number;
}

export interface FLACSampleEntry extends AudioSampleEntry {
  type: 'fLaC';
}


export interface SampleDescriptionBox extends FullBox {
  type: 'stsd';
}


export interface TrackExtendsBox extends FullBox {
  type: 'trex';
  track_ID: number;
  default_sample_description_index: number;
  default_sample_duration: number;
  default_sample_size: number;
  default_sample_flags: number;
}


export interface MovieFragmentHeaderBox extends FullBox {
  type: 'mfhd';
  sequence_number: number;
}


export interface TrackFragmentHeaderBox extends FullBox {
  type: 'tfhd';
  track_ID: number;

  // Optional fields
  base_data_offset?: number;
  sample_description_index?: number;
  default_sample_duration?: number;
  default_sample_size?: number;
  default_sample_flags?: number;
}


export interface TrackFragmentBaseMediaDecodeTimeBox extends FullBox {
  type: 'tfdt';
  baseMediaDecodeTime: number;
}


export interface TrackRunBoxSample {
  sample_duration?: number;
  sample_size?: number;
  sample_flags?: number;
  sample_composition_time_offset?: number;
}

export interface TrackRunBox extends FullBox {
  type: 'trun';
  sample_count: number;

  // Optional fields
  data_offset?: number;
  first_sample_flags?: number;
  samples: TrackRunBoxSample[];
}


export interface TrackFragmentRandomAccessBoxEntry {
  time: number;
  moof_offset: number;
  traf_number: number;
  trun_number: number;
  sample_number: number;
}


export interface TrackFragmentRandomAccessBox extends FullBox {
  type: 'tfra';
  track_ID: number;
  length_size_of_traf_num: number;
  length_size_of_trun_num: number;
  length_size_of_sample_num: number;
  entries: TrackFragmentRandomAccessBoxEntry[];
}


export interface MovieFragmentRandomAccessOffsetBox extends FullBox {
  type: 'mfro';
  size: number;
}


export interface MediaHeaderBox extends FullBox {
  type: 'mdhd';
  creation_time: number;
  modification_time: number;
  timescale: number;
  duration: number;
  language: number;
  languageString: string;
}


export interface HandlerBox extends FullBox {
  type: 'hdlr';
  handler_type: string;
  name: string;
}


export interface SoundMediaHeaderBox extends FullBox {
  type: 'smhd';
  balance: number;
}


export interface DataReferenceBox extends FullBox {
  type: 'dref';
}


export interface DataEntryUrlBox extends FullBox {
  type: 'url ';
  location: string;
}


interface DataEntryUrnBox extends FullBox {
  type: 'urn ';
  name: string;
  location: string;
}


export interface TimeToSampleBox extends FullBox {
  type: 'stts';
  sample_count: number[];
  sample_delta: number[];
}


export interface SyncSampleBox extends FullBox {
  type: 'stss';
  sample_number: number[];
}


export interface CompositionOffsetBox extends FullBox {
  type: 'ctts';
  sample_counts: number[];
  sample_offsets: number[];
}


export interface SampleToChunkBox extends FullBox {
  type: 'stsc';
  first_chunk: number[];
  samples_per_chunk: number[];
  sample_description_index: number[];
}


export interface SampleSizeBox extends FullBox {
  type: 'stsz';
  sample_size: number;
  sample_count: number;
  sample_sizes: number[];
}


export interface CompactSampleSizeBox extends FullBox {
  type: 'stz2';
  field_size: number;
  sample_count: number;
  sample_sizes: number[];
}


export interface ChunkOffsetBox extends FullBox {
  type: 'stco';
  chunk_offsets: number[];
}


export interface ChunkLargeOffsetBox extends FullBox {
  type: 'co64';
  chunk_offsets: number[];
}


/**
 * When we have the moov box.
 */
export interface MoovBoxOk {
  type: 'ok';
  moov: Mp4Box;
}

/**
 * When we have a partial moov box, but need
 * more bytes to read it entirely.
 */
export interface IncompleteMoov {
  type: 'incomplete-moov-box';
  moovBoxEnd: number;
}


/**
 * When we have not found the moov box in the bytes we got,
 * it is likely because the file is not fast started. In
 * such a case, we need to fetch more bytes after the mdat box.
 */
export interface MoovBoxNotFound {
  type: 'moov-not-found';
  nextBoxStart: number;
}

export interface Mp4ParsingError {
  type: 'error';
  error: any;
}


export type MoovFetchingStatus = MoovBoxOk | IncompleteMoov | MoovBoxNotFound | Mp4ParsingError;


/**
 * Tries to read the moov box from the given byte array.
 *
 * If the moov box is onyl partially present in the given byte array,
 * the function returns the exclusive end of the moov box, so that the caller
 * can fetch additional bytes and concatenate to the given byte array before
 * retrying.
 *
 * If the moov box is not found at all in the given array,
 * the function returns null, which means that the mp4 file is not
 * fast started.
 */
export function tryGetMoovBox(bytes: Uint8Array): MoovFetchingStatus {
  const reader = new Mp4Reader(bytes);
  return reader.tryGetMoovBox();
}


export class Mp4Reader {

  private pos = 0;

  constructor(private buffer: Uint8Array) {}

  tryGetMoovBox(): MoovFetchingStatus {
    try {
      while (this.pos < this.buffer.byteLength) {
        const oldPos = this.pos;
        const box = this.readMp4Box(true);
        if (box.type === 'moov') {
          const moovBoxEnd = box.start + box.size;
          if (moovBoxEnd > this.buffer.byteLength) {
            // We don't have enough bytes to read the moov box
            return {
              type: 'incomplete-moov-box',
              moovBoxEnd,
            };
          } else {
            // We have enough bytes
            this.pos = oldPos;
            const moov = this.readMp4Box();
            return {
              type: 'ok',
              moov,
            };
          }
        }
      }
      return {
        type: 'moov-not-found',
        nextBoxStart: this.pos,
      };
    } catch (err) {
      return {
        type: 'error',
        error: err,
      };
    }
  }

  parse(): Mp4Box[] {
    const boxes: Mp4Box[] = [];
    while (this.pos < this.buffer.byteLength) {
      boxes.push(this.readMp4Box());
    }
    return boxes;
  }


  private readMp4Box(ignoreSubBoxes = false): Mp4Box {
    const start = this.pos;
    let size = this.readUint32BE();
    const type = this.readChars(4);

    // Handle extended size (size == 1 means 64-bit size follows)
    if (size === 1) {
      size = this.readUint64BE();
    } else if (size === 0) {
      // Size 0 means everything til the end of the file
      size = this.buffer.byteLength - start;
    }

    const children: Mp4Box[] = [];

    const box: Mp4Box = {
      type,
      size,
      start,
      children,
    };


    const nextBoxPos = start + size;
    switch (type) {
      case 'btrt': return this.readBitRateBox(box);
      case 'dfLa': return this.readFLACSpecificBox(box);
      case 'stco': return this.readChunkOffsetBox(box);
      case 'co64': return this.readChunkLargeOffsetBox(box);
      case 'stsz': return this.readSampleSizeBox(box);
      case 'stz2': return this.readCompactSampleSizeBox(box);
      case 'stsc': return this.readSampleToChunkBox(box);
      case 'stts': return this.readTimeToSampleBox(box);
      case 'stss': return this.readSyncSampleBox(box);
      case 'ctts': return this.readCompositionOffsetBox(box);
      case 'urn ': return this.readDataEntryUrnBox(box);
      case 'url ': return this.readDataEntryUrlBox(box);
      case 'dref': return this.readDataReferenceBox(box);
      case 'smhd': return this.readSoundMediaHeaderBox(box);
      case 'hdlr': return this.readHandlerBox(box);
      case 'mdhd': return this.readMediaHeaderBox(box);
      case 'elst': return this.readEditListBox(box);
      case 'tkhd': return this.readTrackHeaderBox(box);
      case 'mvhd': return this.readMovieHeaderBox(box);
      case 'fLaC': return this.readFLACSampleEntry(box);
      case 'ftyp': return this.readFileTypeBox(box);
      case 'stsd': return this.readSampleDescriptionBox(box);
      case 'trex': return this.readTrackExtendsBox(box);
      case 'mfhd': return this.readMovieFragmentHeaderBox(box);
      case 'tfhd': return this.readTrackFragmentHeaderBox(box);
      case 'tfdt': return this.readTrackFragmentBaseMediaDecodeTimeBox(box);
      case 'trun': return this.readTrackRunBox(box);
      case 'tfra': return this.readTrackFragmentRandomAccessBox(box);
      case 'mfro': return this.readMovieFragmentRandomAccessOffsetBox(box);
      case 'avc1': return this.readAvc1VisualSampleEntryBox(box);
      case 'avcC': return this.readAvcCBox(box);
      case 'colr': return this.readColorInformationBox(box);
      case 'pasp': return this.readPixelAspectRatioBox(box);
      case 'mp4a': return this.readMp4aBox(box);
      case 'esds': return this.readElementaryStreamDescriptorBox(box);
      case 'meta': {
        // We don't try to parse this box because the specification
        // mentions optional fields but does give precise information
        // about when to expect these fields. As a consequence, we cannot
        // guess how to parse the box. Since we don't rely on any data
        // inside it, we just skip the box entirely
        break;
      }
    }


    if (!ignoreSubBoxes && hasSubBoxes(type)) {
      while (this.pos < nextBoxPos) {
        const b = this.readMp4Box();
        children.push(b);
      }
    } else {
      this.pos = nextBoxPos;
    }

    return box;
  }

  private readFullBox(box: Mp4Box): FullBox {
    const version = this.readUint8();
    const a = this.readUint8();
    const b = this.readUint8();
    const c = this.readUint8();
    const flags = (a << 16) | (b << 8) | c;
    return {
      ...box,
      version,
      flags,
    };
  }


  private readSampleEntry(box: Mp4Box): SampleEntry {
    for (let i = 0; i < 6; i++) {
      this.readUint8();
    }
    const data_reference_index = this.readUint16BE();
    return {
      ...box,
      data_reference_index,
    };
  }


  private readEditListBox(box: Mp4Box): EditListBox {
    invariant(box.type === 'elst', `Expected 'elst' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    const entry_count = this.readUint32BE();
    const entries: EditListBoxEntry[] = [];

    for (let i = 0; i < entry_count; i++) {
      let segment_duration: number;
      let media_time: number;
      if (fullBox.version === 1) {
        segment_duration = this.readUint64BE();
        media_time = this.readUint64BE();
      } else {
        segment_duration = this.readUint32BE();
        media_time = this.readUint32BE();
      }

      const media_rate_integer = this.readUint16BE();
      const media_rate_fraction = this.readUint16BE();
      entries.push({
        segment_duration,
        media_time,
        media_rate_integer,
        media_rate_fraction,
      });
    }

    return {
      ...fullBox,
      type: 'elst',
      entries,
    };
  }


  private readMediaHeaderBox(box: Mp4Box): MediaHeaderBox {
    invariant(box.type === 'mdhd', `Expected 'mdhd' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    let creation_time: number;
    let modification_time: number;
    let timescale: number;
    let duration: number;

    if (fullBox.version === 1) {
      creation_time = this.readUint64BE();
      modification_time = this.readUint64BE();
      timescale = this.readUint32BE();
      duration = this.readUint64BE();
    } else {
      creation_time = this.readUint32BE();
      modification_time = this.readUint32BE();
      timescale = this.readUint32BE();
      duration = this.readUint32BE();
    }

    const language = this.readUint16BE();

    // reserved
    this.readUint16BE();

    return {
      ...fullBox,
      type: 'mdhd',
      creation_time,
      modification_time,
      timescale,
      duration,
      language,
      languageString: String.fromCharCode(0x60 + (language >> 10), 0x60 + ((language >> 5) & 0b11111), 0x60 + (language & 0b11111)),
    };
  }


  private readSoundMediaHeaderBox(box: Mp4Box): SoundMediaHeaderBox {
    invariant(box.type === 'smhd', `Expected 'smhd' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    const balance = this.readUint16BE();

    // reserved
    this.readUint16BE();

    return {
      ...fullBox,
      type: 'smhd',
      balance,
    };
  }


  private readDataReferenceBox(box: Mp4Box): DataReferenceBox {
    invariant(box.type === 'dref', `Expected 'dref' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    const entry_count = this.readUint32BE();
    for (let i = 0; i < entry_count; i++) {
      fullBox.children.push(this.readMp4Box());
    }

    return {
      ...fullBox,
      type: 'dref',
    };
  }


  private readDataEntryUrlBox(box: Mp4Box): DataEntryUrlBox {
    invariant(box.type === 'url ', `Expected 'url ' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    let location = '';
    if (fullBox.flags !== 1) {
      location = this.readNullTerminatedUtf8String();
    }

    return {
      ...fullBox,
      type: 'url ',
      location,
    };
  }


  private readTimeToSampleBox(box: Mp4Box): TimeToSampleBox {
    invariant(box.type === 'stts', `Expected 'stts' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    const entry_count = this.readUint32BE();
    const sample_count: number[] = [];
    const sample_delta: number[] = [];

    for (let i = 0; i < entry_count; i++) {
      sample_count.push(this.readUint32BE());
      sample_delta.push(this.readUint32BE());
    }

    return {
      ...fullBox,
      type: 'stts',
      sample_count,
      sample_delta,
    };
  }


  private readSyncSampleBox(box: Mp4Box): SyncSampleBox {
    invariant(box.type === 'stss', `Expected 'stss' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    const entry_count = this.readUint32BE();
    const sample_number: number[] = [];

    for (let i = 0; i < entry_count; i++) {
      sample_number.push(this.readUint32BE());
    }

    return {
      ...fullBox,
      type: 'stss',
      sample_number,
    };
  }


  private readCompositionOffsetBox(box: Mp4Box): CompositionOffsetBox {
    invariant(box.type === 'ctts', `Expected 'ctts' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    const entry_count = this.readUint32BE();
    const sample_counts: number[] = [];
    const sample_offsets: number[] = [];

    for (let i = 0; i < entry_count; i++) {
      sample_counts.push(this.readUint32BE());
      sample_offsets.push(fullBox.version === 0 ? this.readUint32BE() : this.readInt32BE());
    }

    return {
      ...fullBox,
      type: 'ctts',
      sample_counts,
      sample_offsets,
    };
  }


  private readSampleToChunkBox(box: Mp4Box): SampleToChunkBox {
    invariant(box.type === 'stsc', `Expected 'stsc' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    const entry_count = this.readUint32BE();
    const first_chunk: number[] = [];
    const samples_per_chunk: number[] = [];
    const sample_description_index: number[] = [];

    for (let i = 0; i < entry_count; i++) {
      first_chunk.push(this.readUint32BE());
      samples_per_chunk.push(this.readUint32BE());
      sample_description_index.push(this.readUint32BE());
    }

    return {
      ...fullBox,
      type: 'stsc',
      first_chunk,
      samples_per_chunk,
      sample_description_index,
    };
  }


  private readSampleSizeBox(box: Mp4Box): SampleSizeBox {
    invariant(box.type === 'stsz', `Expected 'stsz' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    const sample_size = this.readUint32BE();
    const sample_count = this.readUint32BE();
    const sample_sizes: number[] = [];

    if (sample_size === 0) {
      for (let i = 0; i < sample_count; i++) {
        sample_sizes.push(this.readUint32BE());
      }
    }

    return {
      ...fullBox,
      type: 'stsz',
      sample_size,
      sample_count,
      sample_sizes,
    };
  }


  private readCompactSampleSizeBox(box: Mp4Box): CompactSampleSizeBox {
    invariant(box.type === 'stz2', `Expected 'stz2' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);
    this.readUint8();
    this.readUint8();
    this.readUint8();
    const field_size = this.readUint8();
    invariant(field_size === 4 || field_size === 8 || field_size === 32, () => `Invalid field size ${field_size} in stz2 box`);
    const sample_count = this.readUint32BE();
    const sample_sizes: number[] = [];

    const bitReader = new BitReader(this.buffer.slice(this.pos));
    for (let i = 0; i < sample_count; i++) {
      sample_sizes.push(bitReader.readBits(field_size));
    }

    return {
      ...fullBox,
      type: 'stz2',
      field_size,
      sample_count,
      sample_sizes,
    };
  }


  private readChunkOffsetBox(box: Mp4Box): ChunkOffsetBox {
    invariant(box.type === 'stco', `Expected 'stco' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    const entry_count = this.readUint32BE();
    const chunk_offsets: number[] = [];

    for (let i = 0; i < entry_count; i++) {
      chunk_offsets.push(this.readUint32BE());
    }

    return {
      ...fullBox,
      type: 'stco',
      chunk_offsets,
    };
  }


  private readChunkLargeOffsetBox(box: Mp4Box): ChunkLargeOffsetBox {
    invariant(box.type === 'co64', `Expected 'co64' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    const entry_count = this.readUint32BE();
    const chunk_offsets: number[] = [];

    for (let i = 0; i < entry_count; i++) {
      chunk_offsets.push(this.readUint64BE());
    }

    return {
      ...fullBox,
      type: 'co64',
      chunk_offsets,
    };
  }


  private readFLACSpecificBox(box: Mp4Box): FLACSpecificBox {
    invariant(box.type === 'dfLa', `Expected 'dfLa' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    const flacMetadataBlocks: FLACMetadataBlock[] = [];
    while (this.pos < (fullBox.start + fullBox.size)) {
      const n = this.readUint32BE();
      const a = (n >> 24) & 0xFF;
      const LastMetadataBlockFlag = (a & 0b10000000) !== 0;
      const BlockType = a & 0b01111111;
      const Length = n & 0xFFFFFF;
      const BlockData = this.readUint8Array(Length);
      flacMetadataBlocks.push({
        LastMetadataBlockFlag,
        BlockType,
        BlockData,
      });
    }

    return {
      ...fullBox,
      type: 'dfLa',
      flacMetadataBlocks,
    };
  }


  private readBitRateBox(box: Mp4Box): BitRateBox {
    invariant(box.type === 'btrt', `Expected 'btrt' but got '${box.type}'`);

    const bufferSizeDB = this.readUint32BE();
    const maxBitrate = this.readUint32BE();
    const avgBitrate = this.readUint32BE();

    return {
      ...box,
      type: 'btrt',
      bufferSizeDB,
      maxBitrate,
      avgBitrate,
    };
  }


  private readDataEntryUrnBox(box: Mp4Box): DataEntryUrnBox {
    invariant(box.type === 'urn ', `Expected 'urn ' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    const name = this.readNullTerminatedUtf8String();
    const location = this.readNullTerminatedUtf8String();

    return {
      ...fullBox,
      type: 'urn ',
      name,
      location,
    };
  }


  private readHandlerBox(box: Mp4Box): HandlerBox {
    invariant(box.type === 'hdlr', `Expected 'hdlr' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    // reserved
    this.readUint32BE();
    const handler_type = this.readChars(4);

    // reserved;
    this.readUint32BEArray(3);

    // QuickTime's original hdlr (Apple) historically used a Pascal-style counted string
    // (one length byte followed by the characters, no null terminator).
    const maxBytesInName = (fullBox.start + fullBox.size) - this.pos;
    const name = this.readString(maxBytesInName);

    return {
      ...fullBox,
      type: 'hdlr',
      handler_type,
      name,
    };
  }


  private readTrackHeaderBox(box: Mp4Box): TrackHeaderBox {
    invariant(box.type === 'tkhd', `Expected 'tkhd' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);
    let creation_time: number;
    let modification_time: number;
    let duration: number;
    let track_id: number;

    if (fullBox.version === 1) {
      creation_time = this.readUint64BE();
      modification_time = this.readUint64BE();
      track_id = this.readUint32BE();

      // reserved
      this.readUint32BE();

      duration = this.readUint64BE();
    } else {
      creation_time = this.readUint32BE();
      modification_time = this.readUint32BE();
      track_id = this.readUint32BE();

      // reserved
      this.readUint32BE();

      duration = this.readUint32BE();
    }

    //reserved
    this.readUint32BEArray(2);

    const layer = this.readUint16BE();
    const alternate_group = this.readUint16BE();
    const volume = this.readUint16BE();

    // reserved
    this.readUint16BE();

    const matrix = this.readUint32BEArray(9);
    const width = this.readUint32BE();
    const height = this.readUint32BE();

    return {
      ...fullBox,
      type: 'tkhd',
      creation_time,
      modification_time,
      track_id,
      duration,
      layer,
      alternate_group,
      volume,
      matrix,
      width,
      height,
    };
  }


  private readMovieHeaderBox(box: Mp4Box): MovieHeaderBox {
    invariant(box.type === 'mvhd', `Expected 'mvhd' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);
    let creation_time: number;
    let modification_time: number;
    let timescale: number;
    let duration: number;

    if (fullBox.version === 1) {
      creation_time = this.readUint64BE();
      modification_time = this.readUint64BE();
      timescale = this.readUint64BE();
      duration = this.readUint64BE();
    } else {
      creation_time = this.readUint32BE();
      modification_time = this.readUint32BE();
      timescale = this.readUint32BE();
      duration = this.readUint32BE();
    }

    const rate = this.readUint32BE();
    const volume = this.readUint16BE();

    // reserved
    this.readUint16BE();

    //reserved
    this.readUint32BEArray(2);

    const matrix = this.readUint32BEArray(9);

    // reserved
    this.readUint32BEArray(6);

    const next_track_id = this.readUint32BE();

    return {
      ...fullBox,
      type: 'mvhd',
      creation_time,
      modification_time,
      timescale,
      duration,
      rate,
      volume,
      matrix,
      next_track_id,
    };
  }


  private readVisualSampleEntry(box: Mp4Box): VisualSampleEntry {
    const sampleEntry = this.readSampleEntry(box);

    this.readUint16BE(); // pre_defined 0
    this.readUint16BE(); // reserved 0
    this.readUint32BEArray(3); // pre_defined 0

    const width = this.readUint16BE();
    const height = this.readUint16BE();
    const horizresolution = this.readUint32BE();
    const vertresolution = this.readUint32BE();

    this.readUint32BE(); // reserved 0
    const frame_count = this.readUint16BE();

    // The first byte is the number of chars to display
    const nCompressorNameBytes = this.readUint8();
    const compressorName = this.readChars(nCompressorNameBytes);
    // Let's skip the remainining bytes since the compressorName property
    // always takes 32 bytes
    this.readChars(31 - nCompressorNameBytes);

    const depth = this.readUint16BE();

    this.readUint16BE(); // pre_defined -1

    return {
      ...sampleEntry,
      width,
      height,
      horizresolution,
      vertresolution,
      frame_count,
      compressorName,
      depth,
    };
  }


  private readAvc1VisualSampleEntryBox(box: Mp4Box): Avc1VisualSampleEntry {
    invariant(box.type === 'avc1', `Expected 'avc1' but got '${box.type}'`);
    const visualSampleEntry = this.readVisualSampleEntry(box);

    while (this.pos < box.start + box.size) {
      visualSampleEntry.children.push(this.readMp4Box());
    }

    return {
      ...visualSampleEntry,
      type: 'avc1',
    };
  }


  private readAVCDecoderConfigurationRecord(): AVCDecoderConfigurationRecord {
    const configurationVersion = this.readUint8();
    const AVCProfileIndication = this.readUint8();
    const profile_compatibility = this.readUint8();
    const AVCLevelIndication = this.readUint8();
    const lengthSizeMinusOne = this.readUint8() & 0b11;
    const numOfSequenceParameterSets = this.readUint8() & 0b11111;
    const SPS_nalus: number[][] = [];
    for (let i = 0; i < numOfSequenceParameterSets; i++) {
      const sequenceParameterSetLength = this.readUint16BE();
      const nalHeader = this.readUint8(); // skip NAL unit header (we assume this is SPS);
      const nalUnitType = nalHeader % 32;
      invariant(nalUnitType === 7, `Expected SPS NAL-unit (Nal-unit type: ${nalUnitType})`);
      SPS_nalus.push(this.readUint8Array(sequenceParameterSetLength - 1));
    }
    const numOfPictureParameterSets = this.readUint8();
    const PPS_nalus: number[][] = [];
    for (let i = 0; i < numOfPictureParameterSets; i++) {
      const pictureParameterSetLength = this.readUint16BE();
      const nalHeader = this.readUint8(); // skip NAL unit header (we assume this is SPS);
      const nalUnitType = nalHeader % 32;
      invariant(nalUnitType === 8, `Expected PPS NAL-unit (NAL-unit type: ${nalUnitType})`);
      PPS_nalus.push(this.readUint8Array(pictureParameterSetLength - 1));
    }

    let chroma_format: number | undefined;
    let bit_depth_luma_minus8: number | undefined;
    let bit_depth_chroma_minus8: number | undefined;
    let SPS_ext_nalus: number[][] | undefined;

    if (AVCProfileIndication === 100 || AVCProfileIndication === 110 || AVCProfileIndication === 122 || AVCProfileIndication === 144) {
      chroma_format = this.readUint8() & 0b11;
      bit_depth_luma_minus8 = this.readUint8() & 0b111;
      bit_depth_chroma_minus8 = this.readUint8() & 0b111;
      const numOfSequenceParameterSetExt = this.readUint8();
      SPS_ext_nalus = [];
      for (let i = 0; i < numOfSequenceParameterSetExt; i++) {
        const sequenceParameterSetExtLength = this.readUint16BE();
        SPS_ext_nalus.push(this.readUint8Array(sequenceParameterSetExtLength));
      }
    }
    return {
      configurationVersion,
      AVCProfileIndication,
      profile_compatibility,
      AVCLevelIndication,
      lengthSizeMinusOne,
      SPS_nalus,
      PPS_nalus,
      chroma_format,
      bit_depth_luma_minus8,
      bit_depth_chroma_minus8,
      SPS_ext_nalus,
    };
  }


  private readAvcCBox(box: Mp4Box): AvcC {
    invariant(box.type === 'avcC', `Expected 'avcC' but got '${box.type}'`);
    const n = box.start + box.size - this.pos;
    const pos = this.pos;

    const avcDecoderConfigurationRecord = this.readAVCDecoderConfigurationRecord();

    this.pos = pos;
    const avcC = this.readUint8Array(n);

    return {
      ...box,
      type: 'avcC',
      avcC,
      ...avcDecoderConfigurationRecord,
    };
  }


  private readColorInformationBox(box: Mp4Box): ColorInformationBox {
    invariant(box.type === 'colr', `Expected 'clor' but got '${box.type}'`);
    const n = box.start + box.size - this.pos;
    const pos = this.pos;

    const color_type = this.readChars(4);
    let colour_primaries: number | undefined;
    let transfer_characteristics: number | undefined;
    let matrix_coefficients: number | undefined;
    let full_range_flag: number | undefined;

    if (color_type === 'nclx') {
      colour_primaries = this.readUint16BE();
      transfer_characteristics = this.readUint16BE();
      matrix_coefficients = this.readUint16BE();
      full_range_flag = this.readUint8() >> 7;
    } else if (color_type === 'rICC' || color_type === 'prof') {
      // For now, if we have an ICC profile, we don't try to parse, and
      // just skip the bytes
      this.pos = pos;
      this.readUint8Array(n);
    }

    return {
      ...box,
      type: 'colr',
      color_type,
      colour_primaries,
      transfer_characteristics,
      matrix_coefficients,
      full_range_flag,
    };
  }


  private readPixelAspectRatioBox(box: Mp4Box): PixelAspectRatioBox {
    invariant(box.type === 'pasp', `Expected 'pasp' but got '${box.type}'`);
    const hSpacing = this.readUint32BE();
    const vSpacing = this.readUint32BE();

    return {
      ...box,
      type: 'pasp',
      hSpacing,
      vSpacing,
    };
  }


  private readMp4aBox(box: Mp4Box): Mp4aBox {
    invariant(box.type === 'mp4a', `Expected 'mp4a' but got '${box.type}'`);
    const audioSampleEntry = this.readAudioSampleEntry(box);

    while (this.pos < box.start + box.size) {
      audioSampleEntry.children.push(this.readMp4Box());
    }

    return {
      ...audioSampleEntry,
      type: 'mp4a',
    };
  }


  private readBaseDescriptor(): BaseDescriptorWithSize {
    const tag = this.readUint8();
    const sizeOfInstance = this.readSizeOfInstance();
    return {
      tag,
      sizeOfInstance,
    };
  }


  private readExtendedBIFSConfig(): ExtendedBIFSConfig {
    const base = this.readBaseDescriptor();
    const end = this.pos + base.sizeOfInstance;
    this.pos = end;
    return {
      tag: base.tag,
    };
  }


  private readAFXExtDescriptor(): AFXExtDescriptor {
    const base = this.readBaseDescriptor();
    const end = this.pos + base.sizeOfInstance;
    this.pos = end;
    return {
      tag: base.tag,
    };
  }


  private readDecoderSpecificInfo(oti: number, streamType: number): DecoderSpecificInfo {
    const base = this.readBaseDescriptor();
    const end = this.pos + base.sizeOfInstance;
    invariant(base.tag === DecSpecificInfoTag, () => `Expected tag ${DecSpecificInfoTag} but got ${base.tag}`);
    const decoderSpecificInfo: DecoderSpecificInfo = {
      tag: DecSpecificInfoTag,
    };

    switch (oti) {
      case 0x20: {
        // For values of DecoderConfigDescriptor.objectTypeIndication that refer to streams complying
        // with ISO/IEC 14496-2 the syntax and semantics of decoder specific information are defined in Annex K of that
        // part.
        break;
      }

      case 0x40: {
        // For values of DecoderConfigDescriptor.objectTypeIndication that refer to streams complying
        // with ISO/IEC 14496-3 the syntax and semantics of decoder specific information are defined in subpart 1,
        //subclause 1.6 of that part.
        if (streamType === 0x05) {
          // We have an audio stream
          const pos = this.pos;
          const bitReader = new BitReader(new Uint8Array(this.readUint8Array(end - this.pos)));
          this.pos = pos;
          let audioObjectType = bitReader.readBits(5);
          if (audioObjectType === 31) {
            audioObjectType = 32 + bitReader.readBits(6);
          }
          const samplingFrequencyIndex = bitReader.readBits(4);
          if (samplingFrequencyIndex === 0xF) {
            // samplingFrequency
            bitReader.readBits(24);
          }
          const channelConfiguration = bitReader.readBits(4);
          const audioSpecificConfig: AudioSpecificConfig = {
            ...decoderSpecificInfo,
            audioObjectType,
            samplingFrequencyIndex,
            channelConfiguration,
          };
          return audioSpecificConfig;
        }
        break;
      }

      case 0x01:
      case 0x02: {
        // For values of DecoderConfigDescriptor.objectTypeIndication that refer to scene description
        // streams the semantics of decoder specific information is defined in ISO/IEC 14496-11
        break;
      }

      case 0x66:
      case 0x67:
      case 0x68: {
        // For values of DecoderConfigDescriptor.objectTypeIndication that refer to streams complying with
        // ISO/IEC 13818-7 the decoder specific information consists of an „adif_header()“ and an access unit is a
        // „raw_data_block()“ as defined in ISO/IEC 13818-7.
        break;
      }

      case 0x6B:
      case 0x69: {
        // For values of DecoderConfigDescriptor.objectTypeIndication that refer to streams complying with
        // ISO/IEC 11172-3 or ISO/IEC 13818-3 the decoder specific information is empty since all necessary data is
        // contained in the bitstream frames itself. The access units in this case are the „frame()“ bitstream element as is
        // defined in ISO/IEC 11172-3.
        throw new Error('Should not get here !');
      }

      case 0x6C: {
        const headerLength = this.readUint16BE();
        const Xdensity = this.readUint16BE();
        const Ydensity = this.readUint16BE();
        const numComponents = this.readUint8();
        const jpegDecoderConfig: JPEG_DecoderConfig = {
          ...decoderSpecificInfo,
          headerLength,
          Xdensity,
          Ydensity,
          numComponents,
        };
        return jpegDecoderConfig;
      }

      case 0x03: {
        const deviceNamelength = this.readUint8();
        const deviceName = this.readChars(deviceNamelength);
        const devSpecInfo = this.readUint8Array(base.sizeOfInstance - deviceNamelength - 1);
        const uiConfig: UIConfig = {
          ...decoderSpecificInfo,
          deviceName,
          devSpecInfo,
        };
        return uiConfig;
      }

      case 0x04: {
        const extBIFSConfig = this.readExtendedBIFSConfig();
        const bifsConfigEx: BIFSConfigEx = {
          ...decoderSpecificInfo,
          extBIFSConfig,
        };
        return bifsConfigEx;
      }

      case 0x05: {
        const afxext = this.readAFXExtDescriptor();
        const afxConfig: AFXConfig = {
          ...decoderSpecificInfo,
          afxext,
        };
        return afxConfig;
      }

      case 0x6E: {
        const height = this.readUint32BE();
        const width = this.readUint32BE();
        const nc = this.readUint16BE();
        const BPC = this.readUint8();
        const C = this.readUint8();
        const UnkC = this.readUint8();
        const IPR = this.readUint8();
        const jpeg2000DecoderConfig: JPEG2000_DecoderConfig = {
          ...decoderSpecificInfo,
          height,
          width,
          nc,
          BPC,
          C,
          UnkC,
          IPR,
        };
        return jpeg2000DecoderConfig;
      }
    }

    this.pos = end;
    return decoderSpecificInfo;
  }


  private readProfileLevelIndicationIndexDescriptor(): ProfileLevelIndicationIndexDescriptor {
    const base = this.readBaseDescriptor();
    invariant(base.tag === ProfileLevelIndicationIndexDescrTag, () => `Expected tag ${ProfileLevelIndicationIndexDescrTag} but got ${base.tag}`);

    const profileLevelIndicationIndex = this.readUint8();
    return {
      tag: ProfileLevelIndicationIndexDescrTag,
      profileLevelIndicationIndex,
    };
  }


  private readDecoderConfigDescriptor(): DecoderConfigDescriptor {
    const base = this.readBaseDescriptor();
    const end = this.pos + base.sizeOfInstance;
    invariant(base.tag === DecoderConfigDescrTag, () => `Expected tag ${DecoderConfigDescrTag} but got ${base.tag}`);

    const objectTypeIndication = this.readUint8();
    const u8 = this.readUint8();
    const streamType = (u8 >> 2) & 0b111111;
    const upStream = (u8 >> 1) & 1;
    const bufferSizeDB = this.readUint24BE();
    const maxBitrate = this.readUint32BE();
    const avgBitrate = this.readUint32BE();

    const decSpecificInfo: DecoderSpecificInfo[] = [];
    if (this.pos < end && this.peekUInt8() === DecSpecificInfoTag) {
      decSpecificInfo.push(this.readDecoderSpecificInfo(objectTypeIndication, streamType));
    }

    const profileLevelIndicationIndexDescr: ProfileLevelIndicationIndexDescriptor[] = [];
    while (this.pos < end && this.peekUInt8() === ProfileLevelIndicationIndexDescrTag) {
      profileLevelIndicationIndexDescr.push(this.readProfileLevelIndicationIndexDescriptor());
    }
    this.pos = end;

    return {
      tag: DecoderConfigDescrTag,
      objectTypeIndication,
      streamType,
      upStream,
      bufferSizeDB,
      maxBitrate,
      avgBitrate,
      decSpecificInfo,
      profileLevelIndicationIndexDescr,
    };
  }


  private readDependencyPointer(): DependencyPointer {
    const base = this.readBaseDescriptor();
    invariant(base.tag === DependencyPointerTag, () => `Expected tag ${DependencyPointerTag} but got ${base.tag}`);
    const u8 = this.readUint8();
    const mode = (u8 >> 1) & 1;
    const hasESID = u8 & 1;
    const dependencyLength = this.readUint8();
    let ESID: number | undefined;
    if (hasESID) {
      ESID = this.readUint16BE();
    }

    return {
      tag: DependencyPointerTag,
      mode,
      dependencyLength,
      ESID,
    };
  }


  private readMarkerDescriptor(): MarkerDescriptor {
    const base = this.readBaseDescriptor();
    invariant(base.tag === DependencyMarkerTag, () => `Expected tag ${DependencyMarkerTag} but got ${base.tag}`);
    const markerLength = this.readUint8();
    return {
      tag: DependencyMarkerTag,
      markerLength,
    };
  }


  private readSLExtensionDescriptor(): SLExtensionDescriptor {
    const u8 = this.peekUInt8();
    switch (u8) {
      case DependencyPointerTag: return this.readDependencyPointer();
      case DependencyMarkerTag: return this.readMarkerDescriptor();
      default: throw new Error(`Unexpected tag ${u8} for SLExtensionDescriptor`);
    }
  }


  private readSLConfigDescriptor(): SLConfigDescriptor | ExtendedSLConfigDescriptor {
    const base = this.readBaseDescriptor();
    invariant(base.tag === SLConfigDescrTag || base.tag === ExtSLConfigDescrTag, () => `Expected tag ${SLConfigDescrTag} but got ${base.tag}`);

    const end = this.pos + base.sizeOfInstance;

    let timeScale: number | undefined;
    let accessUnitDuration: number | undefined;
    let compositionUnitDuration: number | undefined;
    let startDecodingTimeStamp: number | undefined;
    let startCompositionTimeStamp: number | undefined;

    let useAccessUnitStartFlag: number;
    let useAccessUnitEndFlag: number;
    let useRandomAccessPointFlag: number;
    let hasRandomAccessUnitsOnlyFlag: number;
    let usePaddingFlag: number;
    let useTimeStampsFlag: number;
    let useIdleFlag: number;
    let durationFlag: number;
    let timeStampResolution: number | undefined;
    let OCRResolution: number | undefined;
    let timeStampLength: number;
    let OCRLength: number | undefined;
    let AU_Length: number;
    let instantBitrateLength: number | undefined;
    let degradationPriorityLength: number;
    let AU_seqNumLength: number;
    let packetSeqNumLength: number;


    const predefined = this.readUint8();
    switch (predefined) {
      case 0: {
        const u8 = this.readUint8();
        useAccessUnitStartFlag = (u8 >> 7) & 1;
        useAccessUnitEndFlag = (u8 >> 6) & 1;
        useRandomAccessPointFlag = (u8 >> 5) & 1;
        hasRandomAccessUnitsOnlyFlag = (u8 >> 4) & 1;
        usePaddingFlag = (u8 >> 3) & 1;
        useTimeStampsFlag = (u8 >> 2) & 1;
        useIdleFlag = (u8 >> 1) & 1;
        durationFlag = u8 & 1;
        timeStampResolution = this.readUint32BE();
        OCRResolution = this.readUint32BE();
        timeStampLength = this.readUint8();
        OCRLength = this.readUint8();
        AU_Length = this.readUint8();
        instantBitrateLength = this.readUint8();
        const u16 = this.readUint16BE();
        degradationPriorityLength = (u16 >> 12) & 0b1111;
        AU_seqNumLength = (u16 >> 7) & 0b11111;
        packetSeqNumLength = (u16 >> 2) & 0b11;
        break;
      }

      case 1: {
        useAccessUnitStartFlag = 0;
        useAccessUnitEndFlag = 0;
        useRandomAccessPointFlag = 0;
        hasRandomAccessUnitsOnlyFlag = 0;
        usePaddingFlag = 0;
        useTimeStampsFlag = 0;
        useIdleFlag = 0;
        durationFlag = 0;
        timeStampResolution = 1000;
        OCRResolution = undefined;
        timeStampLength = 0;
        OCRLength = 0;
        AU_Length = 0;
        instantBitrateLength = 0;
        degradationPriorityLength = 0;
        AU_seqNumLength = 0;
        packetSeqNumLength = 0;
        break;
      }

      case 2: {
        useAccessUnitStartFlag = 0;
        useAccessUnitEndFlag = 0;
        useRandomAccessPointFlag = 0;
        hasRandomAccessUnitsOnlyFlag = 0;
        usePaddingFlag = 0;
        useTimeStampsFlag = 1;
        useIdleFlag = 0;
        durationFlag = 0;
        timeStampResolution = undefined;
        OCRResolution = undefined;
        timeStampLength = 32;
        OCRLength = undefined;
        AU_Length = 0;
        instantBitrateLength = undefined;
        degradationPriorityLength = 0;
        AU_seqNumLength = 0;
        packetSeqNumLength = 0;
        break;
      }

      default: throw new Error(`Unexpected predefined value: ${predefined}`);
    }

    if (durationFlag) {
      timeScale = this.readUint32BE();
      accessUnitDuration = this.readUint16BE();
      compositionUnitDuration = this.readUint16BE();
    }

    if (!useTimeStampsFlag) {
      startDecodingTimeStamp = this.readMultiByteValue(timeStampLength / 8);
      startCompositionTimeStamp = this.readMultiByteValue(timeStampLength / 8);
    }

    const slConfigDescr: SLConfigDescriptor = {
      tag: SLConfigDescrTag,
      useAccessUnitStartFlag,
      useAccessUnitEndFlag,
      useRandomAccessPointFlag,
      hasRandomAccessUnitsOnlyFlag,
      usePaddingFlag,
      useTimeStampsFlag,
      useIdleFlag,
      durationFlag,
      timeStampResolution,
      OCRResolution,
      timeStampLength,
      OCRLength,
      AU_Length,
      instantBitrateLength,
      degradationPriorityLength,
      AU_seqNumLength,
      packetSeqNumLength,

      timeScale,
      accessUnitDuration,
      compositionUnitDuration,
      startDecodingTimeStamp,
      startCompositionTimeStamp,
    };

    let result: SLConfigDescriptor | ExtendedSLConfigDescriptor;
    if (base.tag === SLConfigDescrTag) {
      result = slConfigDescr;
    } else {
      const extSlConfigDescr: ExtendedSLConfigDescriptor = {
        ...slConfigDescr,
        tag: ExtSLConfigDescrTag,
        slextDescr: [],
      };

      while (this.pos < end) {
        extSlConfigDescr.slextDescr.push(this.readSLExtensionDescriptor());
      }

      result = extSlConfigDescr;
    }

    this.pos = end;

    return result;
  }


  private readIPI_DescrPointer(): IPI_DescrPointer {
    const base = this.readBaseDescriptor();
    invariant(base.tag === IPI_DescrPointerTag, () => `Expected tag ${IPI_DescrPointerTag} but got ${base.tag}`);
    const IPI_ES_Id = this.readUint16BE();
    return {
      tag: IPI_DescrPointerTag,
      IPI_ES_Id,
    };
  }


  private readContentIdentificationDescriptor(): ContentIdentificationDescriptor {
    const base = this.readBaseDescriptor();
    invariant(base.tag === ContentIdentDescrTag, () => `Expected tag ${ContentIdentDescrTag} but got ${base.tag}`);
    const u8 = this.readUint8();
    const contentTypeFlag = (u8 >> 5) & 1;
    const contentIdentifierFlag = (u8 >> 4) & 1;
    const protectedContent = (u8 >> 3) & 1;

    let contentType: number | undefined;
    if (contentTypeFlag) {
      contentType = this.readUint8();
    }

    let contentIdentifierType: number | undefined;
    let contentIdentifier: number[] | undefined;
    if (contentIdentifierFlag) {
      contentIdentifierType = this.readUint8();
      contentIdentifier = this.readUint8Array(base.sizeOfInstance - 2 - contentTypeFlag);
    }

    return {
      tag: ContentIdentDescrTag,
      contentType,
      protectedContent,
      contentIdentifierType,
      contentIdentifier,
    };
  }


  private readSupplementaryContentIdentificationDescriptor(): SupplementaryContentIdentificationDescriptor {
    const base = this.readBaseDescriptor();
    invariant(base.tag === SupplContentIdentDescrTag, () => `Expected tag ${SupplContentIdentDescrTag} but got ${base.tag}`);
    const languageCode = this.readChars(3);
    const supplContentIdentifierTitleLength = this.readUint8();
    const supplContentIdentifierTitle = this.readUint8Array(supplContentIdentifierTitleLength);
    const supplContentIdentifierValueLength = this.readUint8();
    const supplContentIdentifierValue = this.readUint8Array(supplContentIdentifierValueLength);
    return {
      tag: SupplContentIdentDescrTag,
      languageCode,
      supplContentIdentifierTitle,
      supplContentIdentifierValue,
    };
  }


  private tryReadIP_IdentificationDataSet(): IP_IdentificationDataSet | undefined {
    const tag = this.peekUInt8();
    switch (tag) {
      case ContentIdentDescrTag: return this.readContentIdentificationDescriptor();
      case SupplContentIdentDescrTag: return this.readSupplementaryContentIdentificationDescriptor();
      default: return undefined;
    }
  }


  private readIPMP_DescriptorPointer(): IPMP_DescriptorPointer {
    const base = this.readBaseDescriptor();
    invariant(base.tag === IPMP_DescrPointerTag, () => `Expected tag ${IPMP_DescrPointerTag} but got ${base.tag}`);
    const IPMP_DescriptorID = this.readUint8();
    let IPMP_DescriptorIDEx: number | undefined;
    let IPMP_ES_ID: number | undefined;
    if (IPMP_DescriptorID === 0xFF) {
      IPMP_DescriptorIDEx = this.readUint16BE();
      IPMP_ES_ID = this.readUint16BE();
    }

    return {
      tag: IPMP_DescrPointerTag,
      IPMP_DescriptorID,
      IPMP_DescriptorIDEx,
      IPMP_ES_ID,
    };
  }


  private readLanguageDescriptor(): LanguageDescriptor {
    const base = this.readBaseDescriptor();
    invariant(base.tag === LanguageDescrTag, () => `Expected tag ${LanguageDescrTag} but got ${base.tag}`);
    const languageCode = this.readChars(3);
    return {
      tag: LanguageDescrTag,
      languageCode,
    };
  }


  private readQoS_Qualifier_MAX_DELAY(): QoS_Qualifier_MAX_DELAY {
    const base = this.readBaseDescriptor();
    invariant(base.tag === 0x01, () => `Expected tag ${0x01} but got ${base.tag}`);
    const MAX_DELAY = this.readUint32BE();
    return {
      tag: 0x01,
      MAX_DELAY,
    };
  }


  private readQoS_Qualifier_PREF_MAX_DELAY(): QoS_Qualifier_PREF_MAX_DELAY {
    const base = this.readBaseDescriptor();
    invariant(base.tag === 0x02, () => `Expected tag ${0x02} but got ${base.tag}`);
    const PREF_MAX_DELAY = this.readUint32BE();
    return {
      tag: 0x02,
      PREF_MAX_DELAY,
    };
  }


  private readQoS_Qualifier_LOSS_PROB(): QoS_Qualifier_LOSS_PROB {
    const base = this.readBaseDescriptor();
    invariant(base.tag === 0x03, () => `Expected tag ${0x03} but got ${base.tag}`);

    // WARNING: this is wrong since the spec says double(32), but I could not
    //          find the definition of what "double(32)" means, so I just read
    //          32 bits here. In the unlikely event that we actually ever need the
    //          value of this property, we would need to come back to this
    const LOSS_PROB = this.readUint32BE();

    return {
      tag: 0x03,
      LOSS_PROB,
    };
  }


  private readQoS_Qualifier_MAX_GAP_LOSS(): QoS_Qualifier_MAX_GAP_LOSS {
    const base = this.readBaseDescriptor();
    invariant(base.tag === 0x04, () => `Expected tag ${0x04} but got ${base.tag}`);
    const MAX_GAP_LOSS = this.readUint32BE();
    return {
      tag: 0x04,
      MAX_GAP_LOSS,
    };
  }


  private readQoS_Qualifier_MAX_AU_SIZE(): QoS_Qualifier_MAX_AU_SIZE {
    const base = this.readBaseDescriptor();
    invariant(base.tag === 0x41, () => `Expected tag ${0x41} but got ${base.tag}`);
    const MAX_AU_SIZE = this.readUint32BE();
    return {
      tag: 0x41,
      MAX_AU_SIZE,
    };
  }


  private readQoS_Qualifier_AVG_AU_SIZE(): QoS_Qualifier_AVG_AU_SIZE {
    const base = this.readBaseDescriptor();
    invariant(base.tag === 0x42, () => `Expected tag ${0x42} but got ${base.tag}`);
    const AVG_AU_SIZE = this.readUint32BE();
    return {
      tag: 0x42,
      AVG_AU_SIZE,
    };
  }


  private readQoS_Qualifier_MAX_AU_RATE(): QoS_Qualifier_MAX_AU_RATE {
    const base = this.readBaseDescriptor();
    invariant(base.tag === 0x43, () => `Expected tag ${0x43} but got ${base.tag}`);
    const MAX_AU_RATE = this.readUint32BE();
    return {
      tag: 0x43,
      MAX_AU_RATE,
    };
  }


  private readQoS_Qualifier_REBUFFERING_RATIO(): QoS_Qualifier_REBUFFERING_RATIO {
    const base = this.readBaseDescriptor();
    invariant(base.tag === 0x44, () => `Expected tag ${0x44} but got ${base.tag}`);
    const REBUFFERING_RATIO = this.readUint8();
    return {
      tag: 0x44,
      REBUFFERING_RATIO,
    };
  }


  private readQoS_Descriptor(): QoS_Descriptor {
    const base = this.readBaseDescriptor();
    invariant(base.tag === QoS_DescrTag, () => `Expected tag ${QoS_DescrTag} but got ${base.tag}`);
    const end = this.pos + base.sizeOfInstance;
    const predefined = this.readUint8();
    const qualifiers: QoS_Qualifier[] = [];
    if (predefined === 0) {
      while (this.pos < end) {
        const u8 = this.peekUInt8();
        switch (u8) {
          case 0x01: qualifiers.push(this.readQoS_Qualifier_MAX_DELAY()); break;
          case 0x02: qualifiers.push(this.readQoS_Qualifier_PREF_MAX_DELAY()); break;
          case 0x03: qualifiers.push(this.readQoS_Qualifier_LOSS_PROB()); break;
          case 0x04: qualifiers.push(this.readQoS_Qualifier_MAX_GAP_LOSS()); break;
          case 0x41: qualifiers.push(this.readQoS_Qualifier_MAX_AU_SIZE()); break;
          case 0x42: qualifiers.push(this.readQoS_Qualifier_AVG_AU_SIZE()); break;
          case 0x43: qualifiers.push(this.readQoS_Qualifier_MAX_AU_RATE()); break;
          case 0x44: qualifiers.push(this.readQoS_Qualifier_REBUFFERING_RATIO()); break;
          default: throw new Error(`Unxpected QoS_Qualifier tag ${u8}`);
        }
      }
    }

    return {
      tag: QoS_DescrTag,
      qualifiers,
    };
  }


  private readExtensionDescriptor(): ExtensionDescriptor | ExtensionProfileLevelDescriptor {
    const base = this.readBaseDescriptor();
    const end = this.pos + base.sizeOfInstance;

    if (base.tag === ExtensionProfileLevelDescrTag) {
      const profileLevelIndicationIndex = this.readUint8();
      const ODProfileLevelIndication = this.readUint8();
      const sceneProfileLevelIndication = this.readUint8();
      const audioProfileLevelIndication = this.readUint8();
      const visualProfileLevelIndication = this.readUint8();
      const graphicsProfileLevelIndication = this.readUint8();
      const MPEGJProfileLevelIndication = this.readUint8();
      const TextProfileLevelIndication = this.readUint8();
      const _3DCProfileLevelIndication = this.readUint8();
      return {
        tag: ExtensionProfileLevelDescrTag,
        profileLevelIndicationIndex,
        ODProfileLevelIndication,
        sceneProfileLevelIndication,
        audioProfileLevelIndication,
        visualProfileLevelIndication,
        graphicsProfileLevelIndication,
        MPEGJProfileLevelIndication,
        TextProfileLevelIndication,
        _3DCProfileLevelIndication,
      };
    } else {
      // This is an unknown extension, let's just skip the bytes
      this.pos = end;
      return base;
    }
  }


  private readRegistrationDescriptor(): RegistrationDescriptor {
    const base = this.readBaseDescriptor();
    invariant(base.tag === RegistrationDescrTag, () => `Expected tag ${RegistrationDescrTag} but got ${base.tag}`);
    const formatIdentifier = this.readUint32BE();
    const additionalIdentificationInfo = this.readUint8Array(base.sizeOfInstance - 4);
    return {
      tag: RegistrationDescrTag,
      formatIdentifier,
      additionalIdentificationInfo,
    };
  }


  private readES_Descriptor(): ES_Descriptor {
    const base = this.readBaseDescriptor();
    invariant(base.tag === ES_DescrTag, () => `Expected tag ${ES_DescrTag} but got ${base.tag}`);

    const end = this.pos + base.sizeOfInstance;

    const ES_ID = this.readUint16BE();
    const u8 = this.readUint8();
    const streamDependenceFlag = (u8 >> 7) & 1;
    const URL_Flag = (u8 >> 6) & 1;
    const OCRstreamFlag = (u8 >> 5) & 1;
    const streamPriority = u8 & 0b11111;

    let dependsOn_ES_ID: number | undefined;
    if (streamDependenceFlag) {
      dependsOn_ES_ID = this.readUint16BE();
    }

    let URLstring: string | undefined;
    if (URL_Flag) {
      const URLlength = this.readUint8();
      URLstring = this.readChars(URLlength);
    }

    let OCR_ES_Id: number | undefined;
    if (OCRstreamFlag) {
      OCR_ES_Id = this.readUint16BE();
    }

    const decConfigDescr = this.readDecoderConfigDescriptor();

    // The specs says that this value depends on ODProfileLevelIndication,
    // but it is unclear where ODProfileLevelIndication comes from,
    // so let's use a readSLConfigDescriptor() function that can handle
    // both the simple and the extended configuration
    /*if (ODProfileLevelIndication === 0x01) {
      slConfigDescr = this.readSLConfigDescriptor();
    } else {
      slConfigDescr = this.readExtendedSLConfigDescriptor();
    }*/
    const slConfigDescr = this.readSLConfigDescriptor();

    const ipiPtr: IPI_DescrPointer[] = [];
    if (this.pos < end && this.peekUInt8() === IPI_DescrPointerTag) {
      ipiPtr.push(this.readIPI_DescrPointer());
    }

    const ipIDS: IP_IdentificationDataSet[] = [];
    while (this.pos < end) {
      const ipID: IP_IdentificationDataSet | undefined = this.tryReadIP_IdentificationDataSet();
      if (ipID) {
        ipIDS.push(ipID);
      } else {
        break;
      }
    }

    const ipmpDescrPtr: IPMP_DescriptorPointer[] = [];
    while (this.pos < end && this.peekUInt8() === IPMP_DescrPointerTag) {
      ipmpDescrPtr.push(this.readIPMP_DescriptorPointer());
    }

    const langDescr: LanguageDescriptor[] = [];
    while (this.pos < end && this.peekUInt8() === LanguageDescrTag) {
      langDescr.push(this.readLanguageDescriptor());
    }

    const qosDescr: QoS_Descriptor[] = [];
    while (this.pos < end && this.peekUInt8() === QoS_DescrTag) {
      qosDescr.push(this.readQoS_Descriptor());
    }

    const regDescr: RegistrationDescriptor[] = [];
    while (this.pos < end && this.peekUInt8() === RegistrationDescrTag) {
      regDescr.push(this.readRegistrationDescriptor());
    }

    const extDescr: ExtensionDescriptor[] = [];
    while (this.pos < end) {
      extDescr.push(this.readExtensionDescriptor());
    }

    // TODO: remove when parsing is complete
    invariant(this.pos === end, `Expected pos to be ${end} but it is actually ${this.pos}`);

    return {
      tag: ES_DescrTag,
      ES_ID,
      streamPriority,
      dependsOn_ES_ID,
      URLstring,
      OCR_ES_Id,
      decConfigDescr,
      slConfigDescr,
      ipiPtr,
      ipIDS,
      ipmpDescrPtr,
      langDescr,
      qosDescr,
      regDescr,
      extDescr,
    };
  }


  private readElementaryStreamDescriptorBox(box: Mp4Box): ElementaryStreamDescriptorBox {
    invariant(box.type === 'esds', () => `Expected 'esds' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    const es = this.readES_Descriptor();

    return {
      ...fullBox,
      type: 'esds',
      es,
    };
  }


  private readAudioSampleEntry(box: Mp4Box): AudioSampleEntry {
    const sampleEntry = this.readSampleEntry(box);

    // reserved
    this.readUint32BEArray(2);

    const channelcount = this.readUint16BE();
    const samplesize = this.readUint16BE();

    // reserved
    this.readUint16BE();
    this.readUint16BE();

    const samplerate = (this.readUint32BE() >> 16) & 0xFFFF;

    return {
      ...sampleEntry,
      channelcount,
      samplesize,
      samplerate,
    };
  }


  private readFLACSampleEntry(box: Mp4Box): FLACSampleEntry {
    invariant(box.type === 'fLaC', `Expected 'fLaC' but got '${box.type}'`);
    const audioSampleEntry = this.readAudioSampleEntry(box);

    while (this.pos < box.start + box.size) {
      audioSampleEntry.children.push(this.readMp4Box());
    }

    return {
      ...audioSampleEntry,
      type: 'fLaC',
    };
  }


  private readSampleDescriptionBox(box: Mp4Box): SampleDescriptionBox {
    invariant(box.type === 'stsd', `Expected 'stsd' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);
    const entry_count = this.readUint32BE();

    for (let i = 0; i < entry_count; i++) {
      fullBox.children.push(this.readMp4Box());
    }
    return {
      ...fullBox,
      type: 'stsd',
    };
  }


  private readTrackExtendsBox(box: Mp4Box): TrackExtendsBox {
    invariant(box.type === 'trex', `Expected 'trex' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    const track_ID = this.readUint32BE();
    const default_sample_description_index = this.readUint32BE();
    const default_sample_duration = this.readUint32BE();
    const default_sample_size = this.readUint32BE();
    const default_sample_flags = this.readUint32BE();

    return {
      ...fullBox,
      type: 'trex',
      track_ID,
      default_sample_description_index,
      default_sample_duration,
      default_sample_size,
      default_sample_flags,
    };
  }


  private readMovieFragmentHeaderBox(box: Mp4Box): MovieFragmentHeaderBox {
    invariant(box.type === 'mfhd', `Expected 'mfhd' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    const sequence_number = this.readUint32BE();
    return {
      ...fullBox,
      type: 'mfhd',
      sequence_number,
    };
  }


  private readTrackFragmentHeaderBox(box: Mp4Box): TrackFragmentHeaderBox {
    invariant(box.type === 'tfhd', `Expected 'tfhd' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    const track_ID = this.readUint32BE();
    let base_data_offset: number | undefined;
    let sample_description_index: number | undefined;
    let default_sample_duration: number | undefined;
    let default_sample_size: number | undefined;
    let default_sample_flags: number | undefined;

    if ((fullBox.flags & 0x000001) !== 0) {
      base_data_offset = this.readUint64BE();
    }

    if ((fullBox.flags & 0x000002) !== 0) {
      sample_description_index = this.readUint32BE();
    }

    if ((fullBox.flags & 0x000008) !== 0) {
      default_sample_duration = this.readUint32BE();
    }

    if ((fullBox.flags & 0x000010) !== 0) {
      default_sample_size = this.readUint32BE();
    }

    if ((fullBox.flags & 0x000020) !== 0) {
      default_sample_flags = this.readUint32BE();
    }

    return {
      ...fullBox,
      type: 'tfhd',
      track_ID,
      base_data_offset,
      sample_description_index,
      default_sample_duration,
      default_sample_size,
      default_sample_flags,
    };
  }


  private readTrackFragmentBaseMediaDecodeTimeBox(box: Mp4Box): TrackFragmentBaseMediaDecodeTimeBox {
    invariant(box.type === 'tfdt', `Expected 'tfdt' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    let baseMediaDecodeTime: number;
    if (fullBox.version === 1) {
      baseMediaDecodeTime = this.readUint64BE();
    } else {
      baseMediaDecodeTime = this.readUint32BE();
    }

    return {
      ...fullBox,
      type: 'tfdt',
      baseMediaDecodeTime,
    };
  }


  private readTrackRunBox(box: Mp4Box): TrackRunBox {
    invariant(box.type === 'trun', `Expected 'trun' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);

    const sample_count = this.readUint32BE();

    let data_offset: number | undefined;
    let first_sample_flags: number | undefined;
    const samples: TrackRunBoxSample[] = [];

    if ((fullBox.flags & 0x000001) !== 0) {
      data_offset = this.readUint32BE();
    }

    if ((fullBox.flags & 0x000004) !== 0) {
      first_sample_flags = this.readUint32BE();
    }

    for (let i = 0; i < sample_count; i++) {
      let sample_duration: number | undefined;
      let sample_size: number | undefined;
      let sample_flags: number | undefined;
      let sample_composition_time_offset: number | undefined;

      if ((fullBox.flags & 0x000100) !== 0) {
        sample_duration = this.readUint32BE();
      }

      if ((fullBox.flags & 0x000200) !== 0) {
        sample_size = this.readUint32BE();
      }

      if ((fullBox.flags & 0x000400) !== 0) {
        sample_flags = this.readUint32BE();
      }

      if ((fullBox.flags & 0x000800) !== 0) {
        sample_composition_time_offset = this.readUint32BE();
      }

      samples.push({
        sample_duration,
        sample_size,
        sample_flags,
        sample_composition_time_offset,
      });
    }

    return {
      ...fullBox,
      type: 'trun',
      sample_count,
      data_offset,
      first_sample_flags,
      samples,
    };
  }


  private readTrackFragmentRandomAccessBox(box: Mp4Box): TrackFragmentRandomAccessBox {
    invariant(box.type === 'tfra', `Expected 'tfra' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);
    const track_ID = this.readUint32BE();

    const n = this.readUint32BE();
    const length_size_of_traf_num = (n & 0b110000) >> 4;
    const length_size_of_trun_num = (n & 0b1100) >> 2;
    const length_size_of_sample_num = (n & 0b11);

    const number_of_entry = this.readUint32BE();
    const entries: TrackFragmentRandomAccessBoxEntry[] = [];

    for (let i = 0; i < number_of_entry; i++) {
      let time: number;
      let moof_offset: number;

      if (fullBox.version === 1) {
        time = this.readUint64BE();
        moof_offset = this.readUint64BE();
      } else {
        time = this.readUint32BE();
        moof_offset = this.readUint32BE();
      }

      const traf_number = this.readMultiByteValue(1 + length_size_of_traf_num);
      const trun_number = this.readMultiByteValue(1 + length_size_of_trun_num);
      const sample_number = this.readMultiByteValue(1 + length_size_of_sample_num);

      entries.push({
        time,
        moof_offset,
        traf_number,
        trun_number,
        sample_number,
      });
    }

    return {
      ...fullBox,
      type: 'tfra',
      track_ID,
      length_size_of_traf_num,
      length_size_of_trun_num,
      length_size_of_sample_num,
      entries,
    };
  }


  private readMovieFragmentRandomAccessOffsetBox(box: Mp4Box): MovieFragmentRandomAccessOffsetBox {
    invariant(box.type === 'mfro', `Expected 'mfro' but got '${box.type}'`);
    const fullBox = this.readFullBox(box);
    const size = this.readUint32BE();
    return {
      ...fullBox,
      type: 'mfro',
      size,
    };
  }


  private readFileTypeBox(box: Mp4Box): FileTypeBox {
    invariant(box.type === 'ftyp', `Expected 'ftyp' but got '${box.type}'`);
    const major_brand = this.readChars(4);
    const minor_version = this.readUint32BE();
    const compatible_brands: string[] = [];
    while (this.pos < (box.start + box.size)) {
      compatible_brands.push(this.readChars(4));
    }
    return {
      ...box,
      type: 'ftyp',
      major_brand,
      minor_version,
      compatible_brands,
    };
  }


  private peekUInt8() {
    if (this.pos >= this.buffer.byteLength) {
      throw new Error('Reached end of buffer');
    }

    return (this.buffer[this.pos]! & 0xFF);
  }


  private readUint8() {
    if (this.pos >= this.buffer.byteLength) {
      throw new Error('Reached end of buffer');
    }

    return (this.buffer[this.pos++]! & 0xFF);
  }


  private readUint16BE() {
    const a = this.readUint8();
    const b = this.readUint8();

    return (a << 8) | b;
  }


  private readMultiByteValue(nBytes: number): number {
    switch(nBytes) {
      case 1: return this.readUint8();
      case 2: return this.readUint16BE();
      case 3: {
        const a = this.readUint8();
        const bc = this.readUint16BE();
        return (a << 16) | bc;
      }
      case 4: return this.readUint32BE();
      default: throw new Error(`Unsupport nBytes value ${nBytes} in readMultiByteValue()`);
    }
  }


  private readUint24BE() {
    return this.readMultiByteValue(3);
  }

  private readUint32BE() {
    const a = this.readUint8();
    const b = this.readUint8();
    const c = this.readUint8();
    const d = this.readUint8();

    return (a * 0x1_00_00_00) + (b * 0x1_00_00) + (c * 0x1_00) + d;
  }


  private readSignedBE(byteCount: number): number {
    const firstByte = this.readUint8();
    const negative = firstByte >= 0x80;
    let value;
    if (negative) {
      value = 255 - firstByte;
      for (let i = 0; i < byteCount - 1; i++) {
        value = (value * 0x100) + (255 - this.readUint8());
      }
      value = (value * -1) - 1;
    } else {
      value = firstByte;
      for (let i = 0; i < byteCount - 1; i++) {
        value = (value * 0x100) + this.readUint8();
      }
    }
    return value;
  }


  private readInt32BE() {
    return this.readSignedBE(4);
  }


  private readUint32BEArray(n: number): number[] {
    const values: number[] = [];
    for (let i = 0; i < n; i++) {
      values.push(this.readUint32BE());
    }
    return values;
  }


  private readUint8Array(n: number): number[] {
    const values: number[] = [];
    for (let i = 0; i < n; i++) {
      values.push(this.readUint8());
    }
    return values;
  }


  private readUint64BE() {
    let value = 0;
    for (let i = 0; i < 8; i++) {
      value = (value * 0x100) + this.readUint8();
    }
    return value;
  }


  /**
   * This function reads a string that MUST be null terminated.
   */
  private readNullTerminatedUtf8String(): string {
    const bytes: number[] = [];
    while (true) {
      const byte = this.readUint8();
      if (byte === 0) {
        break;
      }
      bytes.push(byte);
    }

    return (new TextDecoder()).decode(new Uint8Array(bytes));
  }


  /**
   * This function reads a string that can either be null terminated or Pascal-style,
   * i.e. with no \0 at the end and the first byte being the length in bytes of the string.
   */
  private readString(maxBytesToRead: number): string {
    const bytes: number[] = [];
    while (true) {
      const byte = this.readUint8();
      if (byte === 0) {
        break;
      }
      bytes.push(byte);
      maxBytesToRead--;
      if (maxBytesToRead === 0) {
        break;
      }
    }

    let offset = 0;
    if (maxBytesToRead === 0) {
      // If we have exited the loop because of the maximum byte limit,
      // the string is likely a Pascal-style one. In that case, we need to skip
      // the first byte before trying to decode the remaining bytes as a UTF-8 string
      if (bytes[0] === bytes.length - 1) {
        offset = 1;
      }
    }

    return (new TextDecoder()).decode(new Uint8Array(bytes.slice(offset)));
  }


  private readChars(n: number): string {
    const chars: number[] = [];
    for (let i = 0; i < n; i++) {
      chars.push(this.readUint8());
    }
    return String.fromCharCode(...chars);
  }

  private readSizeOfInstance(): number {
    let u8 = this.readUint8();
    let nextByte = (u8 >> 7) & 0b1;
    let sizeOfInstance = u8 & 0b1111111;
    while (nextByte !== 0) {
      u8 = this.readUint8();
      nextByte = (u8 >> 7) & 0b1;
      const sizeByte = u8 & 0b1111111;
      sizeOfInstance = (sizeOfInstance << 7) | sizeByte;
    }

    return sizeOfInstance;
  }
}


function hasSubBoxes(type: string) {
  switch (type) {
    case 'esds':
    case 'mp4a':
    case 'avc1':
    case 'udta':
    case 'fLaC':
    case 'stsd':
    case 'stbl':
    case 'dinf':
    case 'minf':
    case 'mdia':
    case 'edts':
    case 'trak':
    case 'mvex':
    case 'moof':
    case 'traf':
    case 'mfra':
    case 'moov': return true;
    default: return false;
  }
}
